import { axiosInstance } from './instance';
import { CLIENT_CAMPAIGNS_ROUTE, CLIENTS_ROUTE, CAMPAIGNS_ROUTE, REACH_CAMPAIGNS_ROUTE, CAMPAIGN_DETAIL_ROUTE, CAMPAIGN_IMPRESSIONS_ROUTE, CAMPAIGN_IMPRESSIONS_HOURLY_ROUTE, CAMPAIGN_IMPRESSIONS_HOURLY_AVERAGE_ROUTE, CAMPAIGN_CREATIVES_ROUTE, CAMPAIGN_DISPLAY_UNITS_ROUTE, CAMPAIGN_IMPRESSIONS_BY_DISPLAY_UNIT_ROUTE, CAMPAIGN_IMPRESSIONS_BY_CREATIVE_ROUTE, CAMPAIGN_CREATIVE_LOOKUP_ROUTE, CAMPAIGN_CREATIVES_SUMMARY_ROUTE, CAMPAIGN_CREATIVE_DETAIL_ROUTE, SCREEN_CREATIVES_ROUTE, SCREENS_SUMMARY_ROUTE, CAMPAIGNS_IMPRESSIONS_SUMMARY_ROUTE, CAMPAIGN_DEMOGRAPHICS_ROUTE, CAMPAIGN_CREATIVE_IMPRESSIONS_HOURLY_ROUTE, CAMPAIGN_CREATIVE_IMPRESSIONS_HOURLY_AVERAGE_ROUTE, CAMPAIGN_CREATIVE_SCREENS_ROUTE, SCREENS_ALL_ROUTE } from './consts';
import { withCache } from '../utils/cache';

import { Campaign, ClientRecord, Screen, Demographics } from '../data';

interface APICampaign {
  uuid?: string;
  id?: string;
  name: string;
  client?: ClientRecord;
  status?: string;
  start_date: number | string;
  end_date: number | string;
  budget?: number;
  spent?: number;
  pacing?: number;
  area?: string;
  completion?: number | null;
  attention?: number | null;
  delta?: number;
  thumb?: string;
  image_url?: string;
  featured?: boolean;
  sparkline?: number[];
}

function toDateStr(val: number | string | null | undefined): string {
  if (val == null) return '';
  if (typeof val === 'number') return new Date(val).toISOString().slice(0, 10);
  return val;
}

const DAY_MS = 86_400_000;

function inferStatus(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return 'Active';
  const now = Date.now();
  if (now < new Date(startDate).getTime()) return 'Scheduled';
  if (now >= new Date(endDate).getTime() + DAY_MS) return 'Ended';
  return 'Active';
}

function inferPacing(startDate: string, endDate: string, status: string): number {
  if (status === 'Ended') return 100;
  if (status !== 'Active') return 0;
  if (!startDate || !endDate) return 0;
  const now = Date.now();
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime() + DAY_MS;
  if (!start || !end || end <= start) return 0;
  return Math.min(100, Math.round((now - start) / (end - start) * 100));
}

function formatFlight(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '';
  const fmt = (s: string) =>
    new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

function toCampaign(c: APICampaign): Campaign {
  const startDate = toDateStr(c.start_date);
  const endDate = toDateStr(c.end_date);
  const status = inferStatus(startDate, endDate);
  return {
    id: c.uuid ?? c.id ?? '',
    broadsignId: c.uuid,
    name: c.name,
    client: c.client ?? { uuid: '', identifier: '', name: '' },
    status,
    pacing: inferPacing(startDate, endDate, status),
    startDate,
    endDate,
    flight: formatFlight(startDate, endDate),
    budget: c.budget ?? 0,
    spent: c.spent ?? 0,
    area: c.area ?? '',
    plays: 0,
    impressions: 0,
    measurable: 0,
    views: 0,
    completion: c.completion ?? null,
    attention: c.attention ?? null,
    delta: c.delta ?? 0,
    thumb: c.thumb ?? '',
    imageUrl: c.image_url,
    featured: c.featured,
    sparkline: [],
  };
}

export async function getClients(): Promise<ClientRecord[]> {
  return withCache('clients', async () => {
    try {
      const response = await axiosInstance.get<ClientRecord[]>(CLIENTS_ROUTE);
      if (Array.isArray(response.data) && response.data.length > 0) return response.data;
    } catch {}
    const response = await axiosInstance.get<APICampaign[]>(CAMPAIGNS_ROUTE);
    const seen = new Set<string>();
    return response.data
      .map(c => c.client)
      .filter((c): c is ClientRecord => !!c?.uuid && !seen.has(c.uuid) && !!seen.add(c.uuid));
  });
}

export async function getCampaign(uuid: string): Promise<Campaign> {
  return withCache(`campaign:${uuid}`, async () => {
    const response = await axiosInstance.get<APICampaign>(CAMPAIGN_DETAIL_ROUTE(uuid));
    return toCampaign(response.data);
  });
}

export async function getCampaignsImpressionsSummary(
  uuids: string[]
): Promise<Record<string, { plays: number; impressions: number }>> {
  const key = uuids.slice().sort().join(',');
  return withCache(`impressions-summary:${key}`, async () => {
    const response = await axiosInstance.get<{
      results: { uuid: string; total_plays: number; total_impressions: number }[];
    }>(CAMPAIGNS_IMPRESSIONS_SUMMARY_ROUTE(key));
    const map: Record<string, { plays: number; impressions: number }> = {};
    for (const r of response.data.results ?? []) {
      map[r.uuid] = { plays: r.total_plays ?? 0, impressions: r.total_impressions ?? 0 };
    }
    return map;
  });
}

export async function getCampaignImpressions(
  uuid: string,
  startDate: string,
  endDate?: string
): Promise<{ plays: number; impressions: number }> {
  return withCache(`impressions:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ total_plays: number; total_impressions: number }>(
      CAMPAIGN_IMPRESSIONS_ROUTE(uuid, startDate, endDate)
    );
    return { plays: response.data.total_plays ?? 0, impressions: response.data.total_impressions ?? 0 };
  });
}

export interface HourlyPoint {
  day: string;
  hour_of_day: number;
  total_plays: number;
  total_impressions: number;
}

export async function getCampaignHourlySeries(
  uuid: string,
  startDate: string,
  endDate?: string
): Promise<HourlyPoint[]> {
  return withCache(`hourly:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: HourlyPoint[] }>(
      CAMPAIGN_IMPRESSIONS_HOURLY_ROUTE(uuid, startDate, endDate)
    );
    return response.data.results;
  });
}

export interface HourlyAveragePoint {
  hour_of_day: number;
  hour: string;
  average_plays: number;
  average_impressions: number;
  day_count: number;
}

export async function getCampaignHourlyAverageSeries(
  uuid: string,
  startDate: string,
  endDate?: string
): Promise<HourlyAveragePoint[]> {
  return withCache(`hourly-average:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: HourlyAveragePoint[] }>(
      CAMPAIGN_IMPRESSIONS_HOURLY_AVERAGE_ROUTE(uuid, startDate, endDate)
    );
    return response.data.results;
  });
}

export async function getAllCampaigns(): Promise<Campaign[]> {
  return withCache('all-campaigns', async () => {
    const response = await axiosInstance.get<APICampaign[]>(CAMPAIGNS_ROUTE);
    return response.data.map(toCampaign);
  });
}

export async function getReachCampaigns(): Promise<Campaign[]> {
  return withCache('reach-campaigns', async () => {
    const response = await axiosInstance.get<APICampaign[]>(REACH_CAMPAIGNS_ROUTE);
    return response.data.map(toCampaign);
  });
}

export async function getCampaigns(clientUuid: string): Promise<Campaign[]> {
  return withCache(`campaigns:${clientUuid}`, async () => {
    const response = await axiosInstance.get<APICampaign[]>(CLIENT_CAMPAIGNS_ROUTE(clientUuid));
    return response.data.map(toCampaign);
  });
}

export interface CampaignCreative {
  id: string;
  name?: string;
  type?: string;
  creativeUrl?: string;
}

export async function getCampaignCreatives(uuid: string, startDate: string, endDate?: string): Promise<CampaignCreative[]> {
  return withCache(`creatives:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      creative_ids?: string[];
      creatives?: { creative_id: string; creative_url: string | null; name?: string; type?: string }[];
    }>(CAMPAIGN_CREATIVES_ROUTE(uuid, startDate, endDate));

    let creatives: CampaignCreative[];
    if (response.data.creatives?.length) {
      creatives = response.data.creatives.map(c => ({
        id: c.creative_id,
        name: c.name ?? undefined,
        type: c.type ?? undefined,
        creativeUrl: c.creative_url ?? undefined,
      }));
    } else {
      creatives = (response.data.creative_ids ?? []).map(id => ({ id }));
    }

    const needsLookup = creatives.filter(c => !c.creativeUrl);
    if (needsLookup.length > 0) {
      const lookups = await Promise.allSettled(
        needsLookup.map(async c => {
          const res = await axiosInstance.get<{ creative_url: string | null }>(
            CAMPAIGN_CREATIVE_LOOKUP_ROUTE(c.id)
          );
          return { id: c.id, url: res.data.creative_url ?? undefined };
        })
      );
      const urlMap = new Map<string, string>();
      for (const result of lookups) {
        if (result.status === 'fulfilled' && result.value.url) {
          urlMap.set(result.value.id, result.value.url);
        }
      }
      if (urlMap.size > 0) {
        creatives = creatives.map(c => urlMap.has(c.id) ? { ...c, creativeUrl: urlMap.get(c.id) } : c);
      }
    }

    return creatives;
  });
}

export async function getCampaignDisplayUnitIds(uuid: string, startDate: string, endDate?: string): Promise<string[]> {
  return withCache(`display-units:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ display_unit_ids: string[] }>(CAMPAIGN_DISPLAY_UNITS_ROUTE(uuid, startDate, endDate));
    return response.data.display_unit_ids ?? [];
  });
}

export type CountByDay = { display_unit_id?: string; creative_id?: string; creative_url?: string | null; day: string; count: number; total_impressions: number };
export type EntityTotals = Record<string, { plays: number; impressions: number }>;

function aggregateTotals(results: CountByDay[], idKey: 'display_unit_id' | 'creative_id'): EntityTotals {
  const totals: EntityTotals = {};
  for (const r of results) {
    const id = r[idKey];
    if (!id) continue;
    const existing = totals[id] ?? { plays: 0, impressions: 0 };
    totals[id] = { plays: existing.plays + r.count, impressions: existing.impressions + r.total_impressions };
  }
  return totals;
}

export async function getCampaignImpressionsByDisplayUnit(
  uuid: string, startDate: string, endDate?: string
): Promise<EntityTotals> {
  return withCache(`impressions-by-du:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: CountByDay[] }>(
      CAMPAIGN_IMPRESSIONS_BY_DISPLAY_UNIT_ROUTE(uuid, startDate, endDate)
    );
    return aggregateTotals(response.data.results, 'display_unit_id');
  });
}

export interface CreativeImpressionsResult {
  totals: EntityTotals;
  urlMap: Record<string, string>;
}

export async function getCampaignImpressionsByCreative(
  uuid: string, startDate: string, endDate?: string
): Promise<CreativeImpressionsResult> {
  return withCache(`impressions-by-creative:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: CountByDay[] }>(
      CAMPAIGN_IMPRESSIONS_BY_CREATIVE_ROUTE(uuid, startDate, endDate)
    );
    const results = response.data.results ?? [];
    const urlMap: Record<string, string> = {};
    for (const r of results) {
      if (r.creative_id && r.creative_url) urlMap[r.creative_id] = r.creative_url;
    }
    return { totals: aggregateTotals(results, 'creative_id'), urlMap };
  });
}

export async function getRawImpressionsByCreative(
  uuid: string, startDate: string, endDate?: string
): Promise<CountByDay[]> {
  return withCache(`raw-by-creative:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: CountByDay[] }>(
      CAMPAIGN_IMPRESSIONS_BY_CREATIVE_ROUTE(uuid, startDate, endDate)
    );
    return response.data.results ?? [];
  });
}

export interface CreativeSummary {
  name?: string;
  type?: string;
  creativeUrl?: string;
  plays: number;
  impressions: number;
}

export async function getCampaignCreativesSummary(
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<CreativeSummary[]> {
  return withCache(`creatives-summary:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      results: { name?: string | null; type?: string | null; creative_url?: string | null; total_plays: number; total_impressions: number }[];
    }>(CAMPAIGN_CREATIVES_SUMMARY_ROUTE(campaignUuids, startDate, endDate));
    return (response.data.results ?? []).map(r => ({
      name: r.name ?? undefined,
      type: r.type ?? undefined,
      creativeUrl: r.creative_url ?? undefined,
      plays: r.total_plays ?? 0,
      impressions: r.total_impressions ?? 0,
    }));
  });
}

export async function getRawImpressionsByDisplayUnit(
  uuid: string, startDate: string, endDate?: string
): Promise<CountByDay[]> {
  return withCache(`raw-by-du:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ results: CountByDay[] }>(
      CAMPAIGN_IMPRESSIONS_BY_DISPLAY_UNIT_ROUTE(uuid, startDate, endDate)
    );
    return response.data.results ?? [];
  });
}

export interface CreativeDetailCampaign {
  uuid: string;
  name: string;
  plays: number;
  impressions: number;
}

export interface CreativeDetailScreen {
  id: string;
  name: string;
  address?: string;
  plays: number;
  impressions: number;
  sensorEnabled: boolean;
}

export interface CreativeDetailResult {
  campaigns: CreativeDetailCampaign[];
  screens: CreativeDetailScreen[];
}

export interface SensorLocation {
  key: string;
  label: string;
  screenIds: string[];
  screenNames: string[];
}

// Groups sensor-enabled screens by physical location (address, falling back
// to screen name when no address is known). Co-located screens frequently
// share a single audience sensor, so treating them as one location avoids
// showing the same numbers duplicated once per screen.
export function groupScreensByLocation(screens: CreativeDetailScreen[]): SensorLocation[] {
  const groups = new Map<string, SensorLocation>();
  for (const s of screens) {
    const key = (s.address && s.address.trim()) || s.name;
    const g = groups.get(key);
    if (g) { g.screenIds.push(s.id); g.screenNames.push(s.name); }
    else groups.set(key, { key, label: key, screenIds: [s.id], screenNames: [s.name] });
  }
  return Array.from(groups.values());
}

export async function getCreativeDetail(
  creativeUrl: string,
  campaignUuids: string | undefined,
  startDate: string,
  endDate?: string
): Promise<CreativeDetailResult> {
  return withCache(`creative-detail:${creativeUrl}:${campaignUuids ?? ''}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      campaigns: { uuid: string; name: string; total_plays: number; total_impressions: number }[];
      screens: { display_unit_id: string; name: string; address?: string; sensor_enabled?: boolean; total_plays: number; total_impressions: number }[];
    }>(CAMPAIGN_CREATIVE_DETAIL_ROUTE(creativeUrl, campaignUuids, startDate, endDate));
    return {
      campaigns: (response.data.campaigns ?? []).map(c => ({
        uuid: c.uuid,
        name: c.name,
        plays: c.total_plays ?? 0,
        impressions: c.total_impressions ?? 0,
      })),
      screens: (response.data.screens ?? []).map(s => ({
        id: s.display_unit_id,
        name: s.name,
        address: s.address,
        plays: s.total_plays ?? 0,
        impressions: s.total_impressions ?? 0,
        sensorEnabled: s.sensor_enabled ?? false,
      })),
    };
  });
}

interface APIScreen {
  id?: string | number;
  broadsign_id?: string | number;
  name?: string;
  address?: string;
  venue?: string;
  neighborhood?: string;
  lat?: number;
  latitude?: number;
  lon?: number;
  lng?: number;
  longitude?: number;
  // Some endpoints (e.g. /screens/?broadsign_ids=) return coordinates only as
  // this "(longitude,latitude)" string instead of separate numeric fields.
  geolocation?: string;
}

// Parses a "(-73.993593,40.746397)" string into { lon, lat }. Returns null if
// it doesn't match the expected "(lon,lat)" shape.
function parseGeolocation(geolocation?: string): { lat: number; lon: number } | null {
  if (!geolocation) return null;
  const match = geolocation.match(/\(?\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)?/);
  if (!match) return null;
  const lon = parseFloat(match[1]);
  const lat = parseFloat(match[2]);
  if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
  return { lat, lon };
}

function mapAPIScreen(s: APIScreen): Screen {
  const parsed = parseGeolocation(s.geolocation);
  return {
    id: String(s.broadsign_id ?? s.id ?? ''),
    name: s.name ?? String(s.broadsign_id ?? s.id ?? ''),
    venue: s.address ?? s.venue ?? '',
    neighborhood: s.neighborhood ?? '',
    lat: s.lat ?? s.latitude ?? parsed?.lat ?? 0,
    lon: s.lon ?? s.lng ?? s.longitude ?? parsed?.lon ?? 0,
    plays: 0,
    measurable: 0,
    views: 0,
  };
}

// Looks the campaign's screens up in the full inventory (broadsign/screens/)
// rather than a per-campaign /screens?broadsign_ids= call; getAllScreens is
// cached, so this and the Network Map tab share one request.
export async function getScreensByBroadsignIds(broadsignIds: string[]): Promise<Screen[]> {
  if (!broadsignIds.length) return [];
  const wanted = new Set(broadsignIds.map(String));
  return (await getAllScreens()).filter(s => wanted.has(s.id));
}

// Every screen in the network (the full inventory), not just a campaign's.
// Accepts either a bare array or a DRF-style { results, next } page and
// follows `next` so a paginated endpoint still returns everything.
export async function getAllScreens(): Promise<Screen[]> {
  return withCache('screens:all', async () => {
    const out: APIScreen[] = [];
    let url: string | null = SCREENS_ALL_ROUTE;
    for (let i = 0; url && i < 50; i++) {
      const res: { data: APIScreen[] | { results?: APIScreen[]; next?: string | null } } = await axiosInstance.get(url);
      if (Array.isArray(res.data)) { out.push(...res.data); break; }
      out.push(...(res.data.results ?? []));
      url = res.data.next ?? null;
    }
    return out.map(mapAPIScreen);
  });
}

export interface ScreenSummary {
  id: string;
  name: string;
  venue: string;
  lat: number;
  lon: number;
  plays: number;
  impressions: number;
  sensorEnabled: boolean;
}

export async function getScreensSummary(
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<ScreenSummary[]> {
  return withCache(`screens-summary:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      results: { display_unit_id: string; name: string; address?: string; lat?: number; latitude?: number; lon?: number; lng?: number; longitude?: number; total_plays: number; total_impressions: number; sensor_enabled: boolean }[];
    }>(SCREENS_SUMMARY_ROUTE(campaignUuids, startDate, endDate));
    return (response.data.results ?? []).map(r => ({
      id: r.display_unit_id,
      name: r.name,
      venue: r.address ?? r.name,
      lat: r.lat ?? r.latitude ?? 0,
      lon: r.lon ?? r.lng ?? r.longitude ?? 0,
      plays: r.total_plays ?? 0,
      impressions: r.total_impressions ?? 0,
      sensorEnabled: r.sensor_enabled ?? false,
    }));
  });
}

// Screens a creative ran on, with location + lat/lon in one call — modeled
// directly on getScreensSummary's shape, plus the creative_url match. Replaces
// the old getCreativeDetail-screens + getScreensByBroadsignIds round trip
// (which needed a second call just to backfill lat/lon and had no reliable
// way to drop stale/deleted screens). campaign_uuids is optional, same as
// the other creative-scoped endpoints.
export async function getCreativeScreens(
  creativeUrl: string,
  campaignUuids: string | undefined,
  startDate: string,
  endDate?: string
): Promise<ScreenSummary[]> {
  return withCache(`creative-screens:${creativeUrl}:${campaignUuids ?? ''}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      results: { display_unit_id: string; name: string; address?: string; lat?: number; lon?: number; sensor_enabled: boolean; total_plays: number; total_impressions: number }[];
    }>(CAMPAIGN_CREATIVE_SCREENS_ROUTE(creativeUrl, campaignUuids, startDate, endDate));
    return (response.data.results ?? []).map(r => ({
      id: r.display_unit_id,
      name: r.name,
      venue: r.address ?? r.name,
      lat: r.lat ?? 0,
      lon: r.lon ?? 0,
      plays: r.total_plays ?? 0,
      impressions: r.total_impressions ?? 0,
      sensorEnabled: r.sensor_enabled ?? false,
    }));
  });
}

export async function getScreenCreatives(
  displayUnitId: string,
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<CreativeSummary[]> {
  return withCache(`screen-creatives:${displayUnitId}:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{
      results: { creative_url?: string | null; name?: string | null; type?: string | null; total_plays: number; total_impressions: number }[];
    }>(SCREEN_CREATIVES_ROUTE(displayUnitId, campaignUuids, startDate, endDate));
    return (response.data.results ?? []).map(r => ({
      name: r.name ?? undefined,
      type: r.type ?? undefined,
      creativeUrl: r.creative_url ?? undefined,
      plays: r.total_plays ?? 0,
      impressions: r.total_impressions ?? 0,
    }));
  });
}

export async function getCampaignDemographics(
  uuid: string,
  startDate: string,
  endDate?: string
): Promise<Demographics> {
  return withCache(`demographics:${uuid}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<Demographics>(
      CAMPAIGN_DEMOGRAPHICS_ROUTE(uuid, startDate, endDate)
    );
    return response.data;
  });
}

// Demographics for a group of sensor-enabled screens (typically every screen
// at one physical location, since co-located screens often share a single
// sensor and would otherwise show identical, duplicated cards), scoped to
// one creative. Backend-summed across the given screens, not client-averaged.
export async function getCreativeLocationDemographics(
  displayUnitIds: string[],
  creativeUrl: string,
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<Demographics> {
  const key = displayUnitIds.join(',');
  return withCache(`location-demographics:${key}:${creativeUrl}:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<Demographics>(
      CAMPAIGN_DEMOGRAPHICS_ROUTE(campaignUuids, startDate, endDate, key, creativeUrl)
    );
    return response.data;
  });
}

// Demographics for a creative aggregated across every screen it played on
// (no display_unit_id filter) — a true backend-side SUM across screens,
// not a client-side average of per-screen percentages.
export async function getCreativeDemographics(
  creativeUrl: string,
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<Demographics> {
  return withCache(`creative-demographics:${creativeUrl}:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<Demographics>(
      CAMPAIGN_DEMOGRAPHICS_ROUTE(campaignUuids, startDate, endDate, undefined, creativeUrl)
    );
    return response.data;
  });
}

export interface CreativeHourlyPoint {
  hour: string;
  plays: number;
  impressions: number;
}

// Delivery (plays/impressions) for a creative, bucketed by hour-of-day across
// the whole date range — the delivery-side counterpart to getCreativeDemographics'
// `daypart` signal counts, for the creative detail page's "by daypart" chart.
export async function getCreativeImpressionsByHour(
  creativeUrl: string,
  campaignUuids: string,
  startDate: string,
  endDate?: string
): Promise<CreativeHourlyPoint[]> {
  return withCache(`creative-impressions-hourly:${creativeUrl}:${campaignUuids}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ daypart: { hour: string; plays: number; impressions: number }[] }>(
      CAMPAIGN_CREATIVE_IMPRESSIONS_HOURLY_ROUTE(campaignUuids, creativeUrl, startDate, endDate)
    );
    return response.data.daypart ?? [];
  });
}

// True per-day average delivery for a creative, bucketed by hour-of-day
// (sum over the range / distinct day_count for that hour) — used by the
// creative export report's "When the audience showed up" chart instead of
// a client-computed impressions/play ratio. campaign_uuids is optional here;
// omitting it averages across every campaign the caller can access.
export async function getCreativeImpressionsByHourAverage(
  creativeUrl: string,
  campaignUuids: string | undefined,
  startDate: string,
  endDate?: string
): Promise<HourlyAveragePoint[]> {
  return withCache(`creative-impressions-hourly-average:${creativeUrl}:${campaignUuids ?? ''}:${startDate}:${endDate ?? ''}`, async () => {
    const response = await axiosInstance.get<{ daypart: HourlyAveragePoint[] }>(
      CAMPAIGN_CREATIVE_IMPRESSIONS_HOURLY_AVERAGE_ROUTE(creativeUrl, campaignUuids, startDate, endDate)
    );
    return response.data.daypart ?? [];
  });
}
